import { useEffect, useMemo, useRef, useState } from 'react';
import { useJsApiLoader } from '@react-google-maps/api';

import config from '@occc/config';
import { RetailerNetworkLocationFragment } from '@occc/graphql';
import { StaticMap } from '@occc-app/design-system';

import { resolveLocationsToCoords } from './store-map-section.utils';
import { getStoreMapPinIcon } from './store-map-pin.icon';

const API_LOADER_CONFIG = {
  id: 'google-map-script',
  googleMapsApiKey: config.google.mapsApiKey || '',
};

const THUMBNAIL_ZOOM = 15;

type StoreMapThumbnailProps = {
  location: RetailerNetworkLocationFragment;
  height: number;
};

export const StoreMapThumbnail = ({ location, height }: StoreMapThumbnailProps) => {
  const { isLoaded } = useJsApiLoader(API_LOADER_CONFIG);
  const [coordinates, setCoordinates] = useState<google.maps.LatLngLiteral | null>(null);
  const geocoderRef = useRef<google.maps.Geocoder | null>(null);
  const locationRef = useRef(location);
  locationRef.current = location;

  // Only instantiate after the Maps API is loaded — google.maps.Size/Point require it
  const pinIcon = useMemo(() => (isLoaded ? getStoreMapPinIcon(false) : null), [isLoaded]);

  // Stable primitives derived from the location so the effect doesn't re-run on every
  // Apollo re-render that returns a new object reference for the same data.
  const { uuid } = location;
  const addr = location.location.address;
  const addressKey = [
    addr?.geoLocation?.latitude,
    addr?.geoLocation?.longitude,
    addr?.street1,
    addr?.city,
    addr?.state,
    addr?.zipCode,
  ].join('|');

  useEffect(() => {
    if (!isLoaded) return;
    geocoderRef.current ??= new google.maps.Geocoder();
    let cancelled = false;
    setCoordinates(null);
    resolveLocationsToCoords([locationRef.current], geocoderRef.current).then((pins) => {
      if (cancelled) return;
      if (pins.length > 0) setCoordinates({ lat: pins[0].lat, lng: pins[0].lng });
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, uuid, addressKey]);

  return (
    <StaticMap
      coordinates={coordinates}
      height={height}
      zoom={THUMBNAIL_ZOOM}
      markerIcon={pinIcon}
    />
  );
};
